/* ContentBarRenderer.js */

"use strict";
let _ = require("lodash");
let LocalVeilGL = require('@gluenetworks/local_veil_gl');

class ContentBarRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("content-bar-gl");
        this._rootElem.component = this._model;
        this._items = [];
        this._localVeil = undefined;
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown(){
        let prom = new Promise((resolve/*,reject*/)=>{
            this._unregisterListeners();
            this._removeVeilContent();
            this._removeItems();
            this._removeSections();
            this._removeVeil();
            this._rootElem.remove();
            this._rootElem = null;
            this._items = null;
            this._model = undefined;
            resolve();
        });
        return prom;
    }
    _registerListeners(){
        this._model.on("veil.hide",this._hideVeil = this._hideVeil.bind(this));
        this._model.on("ContentChanged",this._onContentChanged = this._onContentChanged.bind(this));
    }
    _unregisterListeners(){
        this._model.removeListener("veil.hide",this._onVeilCloseClick);
        this._model.removeListener("ContentChanged",this._onContentChanged);
    }
    _renderSections(){
        if(!this._leftSection){
            this._leftSection = document.createElement("content-bar-left-section");
            this._rootElem.appendChild(this._leftSection);
        }
        if(!this._rightSection){
            this._rightSection = document.createElement("content-bar-right-section");
            this._rootElem.appendChild(this._rightSection);
        }
    }
    _removeSections(){
        if(this._leftSection){
            this._leftSection.remove();
            this._leftSection = undefined;
        }
        if(this._rightSection){
            this._rightSection.remove();
            this._rightSection = undefined;
        }
    }
    _renderVeilContent(details){
        if(!this._veilContent){
            this._veilContent = document.createElement("content-bar-veil-contents-gl");
        }
        this._veilContent.innerHTML = details;
    }
    _removeVeilContent(){
        if(this._veilContent){
            this._veilContent.remove();
            this._veilContent = undefined;
        }
    }
    _renderVeil(){
        if(!this._localVeil){
            this._localVeil = new LocalVeilGL({darkTheme: true});
            this._localVeil.on("HideComplete",this._onVeilCloseClick = this._onVeilCloseClick.bind(this));
            this._localVeil.renderer.rootElement.classList.add("content-bar");
            this._model.veilContainer.appendChild(this._localVeil.renderer.rootElement);
        }
    }

    _removeVeil(){
        if(this._localVeil){
            this._model.veilContainer.removeChild(this._localVeil.renderer.rootElement);
            this._localVeil.removeListener("HideComplete",this._onVeilCloseClick);
            this._localVeil.shutdown();
            this._localVeil = undefined;
        }
    }
    _renderItems(){
        this._removeItems();
        if(_.has(this._model,"items") && !_.isEmpty(this._model.items)){
            this._model.items.forEach((item)=>{
                if (!_.isString(item.title)) {
                    return;
                }
                let itemEl;
                if(_.has(item,"details")){
                    itemEl = document.createElement("content-bar-nav-item");
                    itemEl._item = item;
                    itemEl.innerHTML = item.title;
                    itemEl.addEventListener("click",this._toggleDetails = this._toggleDetails.bind(this));
                    this._leftSection.appendChild(itemEl);
                }else{
                    itemEl = document.createElement("content-bar-item");
                    itemEl._item = item;
                    itemEl.innerHTML = item.title;
                    this._rightSection.appendChild(itemEl);
                }
                this._items.push(itemEl);
            });
        }
    }
    _removeItems(){
        let leftSect = this._leftSection;
        while (leftSect.firstChild) {
            leftSect.removeChild(leftSect.firstChild);
        }     
        let rightSect = this._rightSection;
        while (rightSect.firstChild) {
            rightSect.removeChild(rightSect.firstChild);
        }     
    }
    _onContentChanged(){
        this.render();
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render(){
        this._renderSections();
        this._renderVeil();
        this._renderItems();
        this._registerListeners();
        return Promise.resolve(); 
    }   
    get rootElement(){
        return this.getRootElement();
    }
    getRootElement(){
        return this._rootElem;
    }
    get model(){
        return this.getModel();
    }
    getModel(){
        return this._model;
    }

    get veilShown(){
        return this.isVeilShown();
    }
    
    isVeilShown(){
        let isVeilShown = (this._localVeil && this._localVeil.shown);
        return isVeilShown;
    }
    
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    _toggleDetails(evt) {
        let itemEl = _.find(this._items, (iEl)=> {
            let found = iEl === evt.target;
            return found;
        });
        if (itemEl) {
            if(_.isUndefined(this._localVeil) || _.isUndefined(this._localVeil._itemEl)){
               this._showDetails(itemEl);
            }else
            if(this._localVeil.shown){
                if (_.isEqual(evt.target, this._localVeil._itemEl)) {
                    this._hideVeil();
                }else{
                    this._swapDetails(itemEl);
                }
            }else{
                this._showDetails(itemEl);
            }
        }
    }

    _swapDetails(itemEl){
        let newDetails = this._getDetails(itemEl);
        this._renderVeilContent(newDetails);
        this._localVeil.setTitle(itemEl.innerHTML);
        this._localVeil._itemEl = itemEl;
        this._setItemActiveState(itemEl);
    }

    _showDetails(itemEl){
        let details = this._getDetails(itemEl);
        this._renderVeilContent(details);
        this._localVeil.setTitle(itemEl.innerHTML);
        this._localVeil.setContent(this._veilContent);
        this._localVeil._itemEl = itemEl;
        this._localVeil.show();
        this._setItemActiveState(itemEl);
    }

    _hideVeil(){
        // this._removeVeilContent();
        this._localVeil.hide();
        this._localVeil._itemEl = undefined;
        this._setItemActiveState();
    }

    _setItemActiveState(itemEl){
        this._items.forEach((iEl)=>{
            if(iEl.classList.contains("active")){
                iEl.classList.remove("active");
            }
        });
        if(itemEl){
            itemEl.classList.add("active");
        }
    }

    _getDetails(itemEl){
        let details;
        if(_.isFunction(itemEl._item.details)){
            details = itemEl._item.details();
        }else{
            details = itemEl._item.details;
        }
        return details;
    }

    _onVeilCloseClick(){
        this._localVeil._itemEl = undefined;
        this._setItemActiveState();
    }
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}
module.exports=ContentBarRenderer;